import React, { useEffect, useMemo, useState, useRef } from 'react';
import Layout from '../components/Layout';
import { api, fmtEUR, fmtNum, PHASE_LABELS, PHASES_ORDER, PHASE_BG } from '../lib/api';
import { useAuth } from '../lib/auth';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from '../components/ui/sheet';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Plus, Download, Upload, Trash2, FileDown, FileText, Tag } from 'lucide-react';
import { toast } from 'sonner';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import SortHeader, { useSort, sortArr } from '../components/SortHeader';

const months = ['Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin', 'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'];
const years = Array.from({ length: 6 }, (_, i) => new Date().getFullYear() - 2 + i);

export default function Pointages() {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const [salaries, setSalaries] = useState([]);
  const [projets, setProjets] = useState([]);
  const [salarieId, setSalarieId] = useState(isAdmin ? 'all' : user.id);
  const [annee, setAnnee] = useState(String(new Date().getFullYear()));
  const [mois, setMois] = useState(String(new Date().getMonth() + 1));
  const [pointages, setPointages] = useState([]);
  const [history6m, setHistory6m] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ date: '', projet_code: '', heures: 0, phase: 'projet_conception', commentaire: '', salarie_id: '' });
  const fileInputRef = useRef(null);
  const [sortSyn, toggleSyn] = useSort('heures', 'desc');
  const [sortDet, toggleDet] = useSort('date', 'desc');
  // Sélection multiple + édition de phase en masse
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkPhaseOpen, setBulkPhaseOpen] = useState(false);
  const [bulkPhaseValue, setBulkPhaseValue] = useState('projet_conception');
  // Filtres promoteur & projet
  const [promoteurs, setPromoteurs] = useState([]);
  const [promoteurFilter, setPromoteurFilter] = useState('all');
  const [projetFilter, setProjetFilter] = useState('all');

  const loadRefs = async () => {
    const promises = [api.get('/projets')];
    if (isAdmin) {
      promises.push(api.get('/users'));
      promises.push(api.get('/promoteurs'));
    }
    const [pRes, sRes, prRes] = await Promise.all(promises);
    setProjets(pRes.data);
    if (isAdmin) {
      setSalaries(sRes.data);
      setPromoteurs(prRes?.data || []);
    }
  };

  const loadPointages = async () => {
    const params = new URLSearchParams();
    if (isAdmin && salarieId !== 'all') params.set('salarie_id', salarieId);
    if (annee !== 'all') params.set('annee', annee);
    if (mois !== 'all') params.set('mois', mois);
    const { data } = await api.get(`/pointages?${params}`);
    setPointages(data);

    // Load last 6 months for chart (ignore current filter)
    const histParams = new URLSearchParams();
    if (isAdmin && salarieId !== 'all') histParams.set('salarie_id', salarieId);
    const { data: all } = await api.get(`/pointages?${histParams}`);
    // Build 6 last months
    const now = new Date();
    const months6 = [];
    for (let i = 5; i >= 0; i--) {
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
      months6.push({ key: `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`,
                     label: d.toLocaleDateString('fr-FR', { month: 'short', year: '2-digit' }),
                     heures: 0, cout: 0 });
    }
    const mmap = Object.fromEntries(months6.map(m => [m.key, m]));
    for (const p of all) {
      const k = p.date.slice(0, 7);
      if (mmap[k]) {
        mmap[k].heures += p.heures;
        const s = (isAdmin && salarieId === 'all') ? salaries.find(u => u.id === p.salarie_id) : (currentSalarie || user);
        if (s) mmap[k].cout += p.heures * (s.taux_horaire || 0);
      }
    }
    setHistory6m(months6);
  };

  useEffect(() => { loadRefs(); }, []);
  useEffect(() => { loadPointages(); }, [salarieId, annee, mois]);

  const currentSalarie = isAdmin ? (salarieId !== 'all' ? salaries.find(s => s.id === salarieId) : null) : user;
  const projMap = useMemo(() => Object.fromEntries(projets.map(p => [p.code, p])), [projets]);
  const salMap = useMemo(() => Object.fromEntries(salaries.map(s => [s.id, s])), [salaries]);

  // Application des filtres promoteur/projet (côté client)
  const filteredPointages = useMemo(() => {
    return pointages.filter(p => {
      const proj = projMap[p.projet_code];
      if (projetFilter !== 'all' && p.projet_code !== projetFilter) return false;
      if (promoteurFilter !== 'all') {
        if (!proj || proj.promoteur_id !== promoteurFilter) return false;
      }
      return true;
    });
  }, [pointages, promoteurFilter, projetFilter, projMap]);

  const totalHeures = useMemo(() => filteredPointages.reduce((sum, p) => sum + p.heures, 0), [filteredPointages]);

  // Synthèse par projet
  const byProjet = useMemo(() => {
    const map = {};
    for (const p of filteredPointages) {
      const k = p.projet_code;
      if (!map[k]) map[k] = { heures: 0, code: p.projet_code, cout: 0 };
      map[k].heures += p.heures;
      const s = salMap[p.salarie_id];
      if (s) map[k].cout += p.heures * (s.taux_horaire || 0);
    }
    return Object.values(map).map(r => ({ ...r, nom: projMap[r.code]?.nom || '—' }))
                             .sort((a, b) => b.heures - a.heures);
  }, [filteredPointages, projets, salaries, projMap, salMap]);

  const coutTotal = byProjet.reduce((s, p) => s + p.cout, 0);

  const submit = async (e) => {
    e.preventDefault();
    try {
      const targetSalarieId = isAdmin
        ? (form.salarie_id || (salarieId !== 'all' ? salarieId : user.id))
        : user.id;
      await api.post('/pointages', {
        salarie_id: targetSalarieId,
        projet_code: form.projet_code, date: form.date,
        heures: Number(form.heures), phase: form.phase, commentaire: form.commentaire,
      });
      toast.success('Pointage ajouté');
      setOpen(false);
      setForm({ date: '', projet_code: '', heures: 0, phase: 'projet_conception', commentaire: '', salarie_id: '' });
      loadPointages();
    } catch (err) { toast.error(err.response?.data?.detail || 'Erreur'); }
  };

  const remove = async (id) => {
    await api.delete(`/pointages/${id}`);
    toast.success('Supprimé');
    loadPointages();
  };

  const applyBulkPhase = async () => {
    if (selectedIds.size === 0) return;
    try {
      const { data } = await api.patch('/pointages/bulk-phase', {
        phase: bulkPhaseValue,
        pointage_ids: Array.from(selectedIds),
      });
      toast.success(`${data.modified} pointage(s) mis à jour : ${PHASE_LABELS[data.phase] || data.phase}`);
      setSelectedIds(new Set());
      setBulkPhaseOpen(false);
      loadPointages();
    } catch (err) {
      console.error('[Pointages] bulk-phase failed', err);
      toast.error(err.response?.data?.detail || 'Erreur');
    }
  };

  const downloadFile = async (url, filename) => {
    const res = await api.get(url, { responseType: 'blob' });
    const blob = new Blob([res.data]);
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = filename;
    link.click();
  };

  const exportExcel = () => {
    const id = isAdmin ? (salarieId !== 'all' ? salarieId : user.id) : user.id;
    const params = new URLSearchParams();
    if (annee !== 'all') params.set('annee', annee);
    if (mois !== 'all') params.set('mois', mois);
    downloadFile(`/pointages/export-excel/${id}?${params}`, `Pointage_${annee}_${mois}.xlsx`);
  };

  const exportPdf = () => {
    const id = isAdmin ? (salarieId !== 'all' ? salarieId : user.id) : user.id;
    if (annee === 'all' || mois === 'all') {
      toast.error('Choisissez un mois et année précis pour le PDF');
      return;
    }
    downloadFile(`/pointages/pdf/${id}?annee=${annee}&mois=${mois}`, `Pointage_${annee}_${mois}.pdf`);
  };

  const exportInstructionsPaye = async () => {
    if (annee === 'all' || mois === 'all') {
      toast.error('Choisissez un mois et année précis pour l\'export instructions de paye');
      return;
    }
    try {
      await downloadFile(
        `/pointages/instructions-paye-zip?annee=${annee}&mois=${mois}`,
        `Instructions_paye_${annee}_${String(mois).padStart(2, '0')}.zip`,
      );
      toast.success('Archive ZIP téléchargée');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur génération PDF');
    }
  };

  const importExcel = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const id = isAdmin ? (salarieId !== 'all' ? salarieId : user.id) : user.id;
    const fd = new FormData();
    fd.append('file', file);
    try {
      const { data } = await api.post(`/pointages/import-excel/${id}`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success(`${data.inserted} pointages importés`);
      if (data.errors?.length) toast.error(`${data.errors.length} erreurs`);
      loadPointages();
    } catch (err) { toast.error(err.response?.data?.detail || 'Erreur import'); }
    e.target.value = '';
  };

  const newDate = () => {
    const y = annee !== 'all' ? Number(annee) : new Date().getFullYear();
    const m = mois !== 'all' ? Number(mois) : new Date().getMonth() + 1;
    return new Date(y, m - 1, 1).toISOString().slice(0, 10);
  };

  // Sort pointages by date desc — utilise filteredPointages
  const sortedPointages = useMemo(() =>
    [...filteredPointages].sort((a, b) => b.date.localeCompare(a.date) || a.projet_code.localeCompare(b.projet_code)),
  [filteredPointages]);

  return (
    <Layout
      title={isAdmin ? 'Pointages' : 'Mes pointages'}
      subtitle={currentSalarie ? `${currentSalarie.prenom} ${currentSalarie.nom}` : (isAdmin && salarieId === 'all' ? 'Tous les salariés' : '')}
      actions={
        <div className="flex gap-2">
          {isAdmin && salarieId !== 'all' && (
            <>
              <Button variant="outline" className="rounded-none border-zinc-900" onClick={() => fileInputRef.current?.click()} data-testid="import-excel-btn">
                <Upload size={14} className="mr-2" /> Import
              </Button>
              <input ref={fileInputRef} type="file" accept=".xlsx,.xls" className="hidden" onChange={importExcel} />
            </>
          )}
          <Button variant="outline" className="rounded-none border-zinc-900" onClick={exportExcel} data-testid="export-excel-btn">
            <Download size={14} className="mr-2" /> Excel
          </Button>
          <Button variant="outline" className="rounded-none border-zinc-900" onClick={exportPdf} data-testid="export-pdf-btn">
            <FileDown size={14} className="mr-2" /> PDF
          </Button>
          {isAdmin && (
            <Button variant="outline" className="rounded-none border-violet-700 text-violet-700 hover:bg-violet-50" onClick={exportInstructionsPaye} data-testid="export-instructions-paye-btn"
              title="Génère un ZIP de PDF (1 par salarié) — heures totales par jour, sans détail projet">
              <FileText size={14} className="mr-2" /> Instructions de paye
            </Button>
          )}
          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild>
              <Button className="rounded-none bg-[#002FA7] hover:bg-[#001B63]"
                onClick={() => setForm({ date: newDate(), projet_code: '', heures: 0, phase: 'projet_conception', commentaire: '', salarie_id: (isAdmin && salarieId !== 'all') ? salarieId : (isAdmin ? '' : user.id) })}
                data-testid="nouveau-pointage-btn">
                <Plus size={14} className="mr-2" /> Pointage
              </Button>
            </SheetTrigger>
            <SheetContent className="rounded-none w-full sm:max-w-md">
              <SheetHeader><SheetTitle className="font-display font-bold text-2xl tracking-tight">Nouveau pointage</SheetTitle></SheetHeader>
              <form onSubmit={submit} className="space-y-4 mt-6">
                {isAdmin && (
                  <div>
                    <Label>Salarié *</Label>
                    <Select value={form.salarie_id} onValueChange={(v) => setForm({ ...form, salarie_id: v })}>
                      <SelectTrigger className="rounded-none" data-testid="pointage-salarie-select"><SelectValue placeholder="Sélectionner un salarié" /></SelectTrigger>
                      <SelectContent className="rounded-none max-h-80">
                        {salaries.map(s => <SelectItem key={s.id} value={s.id}>{s.prenom} {s.nom}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                )}
                <div><Label>Date *</Label><Input required type="date" className="rounded-none font-mono" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} data-testid="pointage-date-input" /></div>
                <div>
                  <Label>Projet *</Label>
                  <Select value={form.projet_code} onValueChange={(v) => setForm({ ...form, projet_code: v })}>
                    <SelectTrigger className="rounded-none" data-testid="pointage-projet-select"><SelectValue placeholder="Sélectionner" /></SelectTrigger>
                    <SelectContent className="rounded-none max-h-80">
                      {projets.map((p) => <SelectItem key={p.code} value={p.code}>{p.nom} <span className="font-mono text-xs text-zinc-500 ml-1">({p.code})</span></SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div><Label>Heures *</Label><Input required type="number" step="0.25" min="0" className="rounded-none font-mono" value={form.heures} onChange={(e) => setForm({ ...form, heures: e.target.value })} data-testid="pointage-heures-input" /></div>
                  <div>
                    <Label>Phase</Label>
                    <Select value={form.phase} onValueChange={(v) => setForm({ ...form, phase: v })}>
                      <SelectTrigger className="rounded-none"><SelectValue /></SelectTrigger>
                      <SelectContent className="rounded-none">
                        {PHASES_ORDER.map(p => <SelectItem key={p} value={p}>{PHASE_LABELS[p]}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div><Label>Commentaire</Label><Input className="rounded-none" value={form.commentaire} onChange={(e) => setForm({ ...form, commentaire: e.target.value })} /></div>
                <Button type="submit" className="w-full rounded-none bg-[#002FA7] hover:bg-[#001B63]" data-testid="pointage-submit-btn">Ajouter</Button>
              </form>
            </SheetContent>
          </Sheet>
        </div>
      }
    >
      {/* Filtres */}
      <div className="bg-white border border-zinc-200 p-4 mb-6 flex flex-wrap gap-4 items-end">
        {isAdmin && (
          <div className="flex-1 min-w-[200px]">
            <Label className="text-[10px] uppercase tracking-[0.15em] text-zinc-600">Salarié</Label>
            <Select value={salarieId} onValueChange={setSalarieId}>
              <SelectTrigger className="rounded-none" data-testid="pointages-salarie-select"><SelectValue /></SelectTrigger>
              <SelectContent className="rounded-none">
                <SelectItem value="all">Tous les salariés</SelectItem>
                {salaries.map((s) => <SelectItem key={s.id} value={s.id}>{s.prenom} {s.nom}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        )}
        <div>
          <Label className="text-[10px] uppercase tracking-[0.15em] text-zinc-600">Mois</Label>
          <Select value={mois} onValueChange={setMois}>
            <SelectTrigger className="rounded-none w-40" data-testid="pointages-mois-select"><SelectValue /></SelectTrigger>
            <SelectContent className="rounded-none">
              <SelectItem value="all">Tous les mois</SelectItem>
              {months.map((m, i) => <SelectItem key={i + 1} value={String(i + 1)}>{m}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-[10px] uppercase tracking-[0.15em] text-zinc-600">Année</Label>
          <Select value={annee} onValueChange={setAnnee}>
            <SelectTrigger className="rounded-none w-32" data-testid="pointages-annee-select"><SelectValue /></SelectTrigger>
            <SelectContent className="rounded-none">
              <SelectItem value="all">Toutes</SelectItem>
              {years.map((y) => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        {isAdmin && (
          <>
            <div>
              <Label className="text-[10px] uppercase tracking-[0.15em] text-zinc-600">Promoteur</Label>
              <Select value={promoteurFilter} onValueChange={(v) => { setPromoteurFilter(v); if (v !== 'all' && projetFilter !== 'all') { const p = projets.find(x => x.code === projetFilter); if (!p || p.promoteur_id !== v) setProjetFilter('all'); } }}>
                <SelectTrigger className="rounded-none w-48" data-testid="pointages-promoteur-select"><SelectValue /></SelectTrigger>
                <SelectContent className="rounded-none max-h-80">
                  <SelectItem value="all">Tous</SelectItem>
                  {promoteurs.map((p) => <SelectItem key={p.id} value={p.id}>{p.nom}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-[0.15em] text-zinc-600">Projet</Label>
              <Select value={projetFilter} onValueChange={setProjetFilter}>
                <SelectTrigger className="rounded-none w-56" data-testid="pointages-projet-select"><SelectValue /></SelectTrigger>
                <SelectContent className="rounded-none max-h-80">
                  <SelectItem value="all">Tous</SelectItem>
                  {projets.filter(p => promoteurFilter === 'all' || p.promoteur_id === promoteurFilter).map((p) => (
                    <SelectItem key={p.code} value={p.code}>{p.code} — {p.nom}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </>
        )}
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-6">
        <div className="bg-white border border-zinc-200 border-l-4 border-l-[#002FA7] p-6" data-testid="total-heures-card">
          <div className="text-[10px] text-zinc-500 uppercase tracking-[0.2em] mb-2">Total heures</div>
          <div className="font-mono font-medium text-3xl">{fmtNum(totalHeures, 1)} h</div>
        </div>
        {isAdmin && (
          <div className="bg-white border border-zinc-200 p-6">
            <div className="text-[10px] text-zinc-500 uppercase tracking-[0.2em] mb-2">Coût total</div>
            <div className="font-mono font-medium text-3xl">{fmtEUR(coutTotal)}</div>
          </div>
        )}
        <div className="bg-white border border-zinc-200 p-6">
          <div className="text-[10px] text-zinc-500 uppercase tracking-[0.2em] mb-2">Projets distincts</div>
          <div className="font-mono font-medium text-3xl">{byProjet.length}</div>
        </div>
        <div className="bg-white border border-zinc-200 p-6">
          <div className="text-[10px] text-zinc-500 uppercase tracking-[0.2em] mb-2">Nb saisies</div>
          <div className="font-mono font-medium text-3xl">{pointages.length}</div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
        {/* Synthèse */}
        <div className="lg:col-span-2 bg-white border border-zinc-200">
          <div className="border-b border-zinc-900 px-4 py-3">
            <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-600 font-medium">Synthèse par projet</div>
          </div>
          <div className="max-h-[600px] overflow-y-auto">
            <table className="w-full text-sm">
              <thead className="sticky top-0 bg-white">
                <tr className="border-b border-zinc-200">
                  <SortHeader sort={sortSyn} onToggle={toggleSyn} k="nom" label="Projet" />
                  <SortHeader sort={sortSyn} onToggle={toggleSyn} k="heures" label="Heures" align="right" />
                  {isAdmin && <SortHeader sort={sortSyn} onToggle={toggleSyn} k="cout" label="Coût" align="right" />}
                </tr>
              </thead>
              <tbody>
                {sortArr(byProjet, sortSyn, (p, k) => p[k]).map((p) => (
                  <tr key={p.code} className="border-b border-zinc-100">
                    <td className="px-4 py-2">
                      <div className="font-medium">{p.nom}</div>
                      <div className="font-mono text-[10px] text-zinc-500">{p.code}</div>
                    </td>
                    <td className="px-4 py-2 font-mono text-right">{fmtNum(p.heures, 1)}</td>
                    {isAdmin && <td className="px-4 py-2 font-mono text-right text-xs">{fmtEUR(p.cout)}</td>}
                  </tr>
                ))}
                {byProjet.length === 0 && <tr><td colSpan={isAdmin ? 3 : 2} className="px-4 py-8 text-center text-zinc-500 text-sm">Aucun pointage.</td></tr>}
              </tbody>
            </table>
          </div>
        </div>

        {/* Détail complet */}
        <div className="lg:col-span-3 bg-white border border-zinc-200">
          <div className="border-b border-zinc-900 px-4 py-3 flex items-center justify-between gap-2">
            <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-600 font-medium">Détail des pointages ({sortedPointages.length})</div>
            {isAdmin && selectedIds.size > 0 && (
              <Dialog open={bulkPhaseOpen} onOpenChange={setBulkPhaseOpen}>
                <DialogTrigger asChild>
                  <Button size="sm" className="rounded-none h-7 px-3 bg-[#002FA7] hover:bg-[#001B63] text-xs" data-testid="bulk-phase-btn">
                    <Tag size={12} className="mr-1.5" />
                    Phase ({selectedIds.size})
                  </Button>
                </DialogTrigger>
                <DialogContent className="rounded-none">
                  <DialogHeader><DialogTitle className="font-display font-bold text-xl">Modifier la phase</DialogTitle></DialogHeader>
                  <div className="space-y-4 mt-4">
                    <p className="text-sm text-zinc-600">
                      Cette action mettra à jour la phase de <span className="font-bold">{selectedIds.size}</span> pointage(s) sélectionné(s).
                    </p>
                    <div>
                      <Label className="text-[10px] uppercase tracking-[0.15em] text-zinc-600">Nouvelle phase</Label>
                      <Select value={bulkPhaseValue} onValueChange={setBulkPhaseValue}>
                        <SelectTrigger className="rounded-none" data-testid="bulk-phase-select"><SelectValue /></SelectTrigger>
                        <SelectContent className="rounded-none">
                          {PHASES_ORDER.map(p => <SelectItem key={p} value={p}>{PHASE_LABELS[p]}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="flex gap-2 justify-end pt-2">
                      <Button variant="outline" className="rounded-none" onClick={() => setBulkPhaseOpen(false)}>Annuler</Button>
                      <Button className="rounded-none bg-[#002FA7] hover:bg-[#001B63]" onClick={applyBulkPhase} data-testid="bulk-phase-apply-btn">Appliquer</Button>
                    </div>
                  </div>
                </DialogContent>
              </Dialog>
            )}
          </div>
          <div className="max-h-[600px] overflow-y-auto">
            <table className="w-full text-sm" data-testid="pointages-table">
              <thead className="sticky top-0 bg-white z-10">
                <tr className="border-b border-zinc-200">
                  {isAdmin && (
                    <th className="px-2 py-2 w-[30px] text-center">
                      <input type="checkbox"
                        checked={sortedPointages.length > 0 && selectedIds.size === sortedPointages.length}
                        onChange={(e) => {
                          if (e.target.checked) setSelectedIds(new Set(sortedPointages.map(p => p.id)));
                          else setSelectedIds(new Set());
                        }}
                        data-testid="bulk-select-all"
                      />
                    </th>
                  )}
                  <th className="px-0 py-0 align-top text-left"><div className="resize-x overflow-hidden min-w-[90px] px-3 py-2 border-r border-zinc-100"><SortHeader sort={sortDet} onToggle={toggleDet} k="date" label="Date" /></div></th>
                  {isAdmin && salarieId === 'all' && <th className="px-0 py-0 align-top text-left"><div className="resize-x overflow-hidden min-w-[80px] px-3 py-2 border-r border-zinc-100"><SortHeader sort={sortDet} onToggle={toggleDet} k="salarie_name" label="Salarié" /></div></th>}
                  <th className="px-0 py-0 align-top text-left"><div className="resize-x overflow-hidden min-w-[160px] px-3 py-2 border-r border-zinc-100"><SortHeader sort={sortDet} onToggle={toggleDet} k="projet_nom" label="Projet" /></div></th>
                  <th className="px-3 py-2 text-left text-[10px] uppercase tracking-[0.15em] text-zinc-600 font-medium">Phase</th>
                  <th className="px-0 py-0 align-top text-right"><div className="resize-x overflow-hidden min-w-[70px] px-3 py-2 border-r border-zinc-100"><SortHeader sort={sortDet} onToggle={toggleDet} k="heures" label="Heures" align="right" /></div></th>
                  <th className="px-3 py-2 w-[40px]"></th>
                </tr>
              </thead>
              <tbody>
                {sortArr(sortedPointages, sortDet, (p, k) => {
                  if (k === 'projet_nom') return projMap[p.projet_code]?.nom || '';
                  if (k === 'salarie_name') { const s = salMap[p.salarie_id]; return s ? s.prenom : ''; }
                  return p[k];
                }).map((p) => {
                  const pr = projMap[p.projet_code];
                  const sal = salMap[p.salarie_id];
                  const phaseKey = p.phase || 'projet_conception';
                  const phaseLbl = PHASE_LABELS[phaseKey] || phaseKey;
                  const phaseBg = PHASE_BG[phaseKey] || 'bg-zinc-50 text-zinc-600 border-zinc-300';
                  return (
                    <tr key={p.id} className="border-b border-zinc-100 hover:bg-zinc-50">
                      {isAdmin && (
                        <td className="px-2 py-2 text-center">
                          <input type="checkbox"
                            checked={selectedIds.has(p.id)}
                            onChange={(e) => {
                              const next = new Set(selectedIds);
                              if (e.target.checked) next.add(p.id);
                              else next.delete(p.id);
                              setSelectedIds(next);
                            }}
                            data-testid={`bulk-select-${p.id}`}
                          />
                        </td>
                      )}
                      <td className="px-3 py-2 font-mono text-xs whitespace-nowrap">{p.date}</td>
                      {isAdmin && salarieId === 'all' && <td className="px-3 py-2 text-xs">{sal ? `${sal.prenom}` : '—'}</td>}
                      <td className="px-3 py-2">
                        <div className="font-medium text-xs">{pr?.nom || '—'}</div>
                        <div className="font-mono text-[10px] text-zinc-500">{p.projet_code}</div>
                      </td>
                      <td className="px-3 py-2">
                        <span className={`inline-block px-2 py-0.5 text-[10px] uppercase tracking-wider font-medium border ${phaseBg}`}>
                          {phaseLbl}
                        </span>
                      </td>
                      <td className="px-3 py-2 font-mono text-right text-xs">{fmtNum(p.heures, 1)}</td>
                      <td className="px-3 py-2 text-right">
                        <button onClick={() => remove(p.id)} className="p-1 hover:bg-rose-100 text-rose-600">
                          <Trash2 size={11} />
                        </button>
                      </td>
                    </tr>
                  );
                })}
                {sortedPointages.length === 0 && <tr><td colSpan={isAdmin ? 7 : 5} className="px-4 py-8 text-center text-zinc-500 text-sm">Aucun pointage.</td></tr>}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </Layout>
  );
}
